/**
 * Staff user detail API service
 *
 * The 401 handling deliberately matches adminService: clear the stored session
 * and send the operator to login. 403 is different — it means the permission
 * was revoked, and the page renders a 404 rather than leaking that the route
 * exists, so it needs its own error type to distinguish it from a generic
 * failure.
 */

import { raiseSessionExpired } from './sessionExpiry';

const API_BASE_URL = '/api';

export class ForbiddenError extends Error {
  constructor(message = 'Forbidden') {
    super(message);
    this.name = 'ForbiddenError';
  }
}

// Re-exported so existing callers that import SessionExpiredError from here
// keep working; the class itself now lives in sessionExpiry.js alongside the
// 401 handling shared with adminService.
export { SessionExpiredError } from './sessionExpiry';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

// A plain-text error body longer than this reads as something other than the
// API's own short, hand-written message — a proxy's HTML error page, for
// instance — so it is not fit to show an operator as-is.
const MAX_ERROR_MESSAGE_LENGTH = 300;

/**
 * Turn a non-OK response into the right error. Always throws — including for
 * 401, which redirects to /login first and then throws anyway, so a caller's
 * ordinary catch block has something to catch rather than a magic return
 * value to remember to check.
 *
 * The API (admin_ctrl.go, role_ctrl.go) answers every error with
 * `c.String(...)`, i.e. a text/plain body, never JSON — so the fallback below
 * reads the body as text, not JSON.
 */
const raise = async (response, messages = {}) => {
  if (response.status === 401) {
    raiseSessionExpired();
  }

  if (response.status === 403) {
    throw new ForbiddenError();
  }

  const specific = messages[response.status];
  if (specific) {
    throw new Error(specific);
  }

  const text = (await response.text().catch(() => '')).trim();
  const fallback = `HTTP ${response.status}`;
  throw new Error(text && text.length <= MAX_ERROR_MESSAGE_LENGTH ? text : fallback);
};

export const getUserDetail = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    // A non-numeric :id also answers 400 ("user id must be a number") — that
    // reads the same as an unknown id to an operator, so it maps to the same
    // message rather than surfacing a raw HTTP 400.
    await raise(response, { 400: 'User not found', 404: 'User not found' });
  }

  return response.json();
};

export const getRoles = async () => {
  const response = await fetch(`${API_BASE_URL}/staff/roles`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    await raise(response);
  }

  const data = await response.json();
  return data.roles || [];
};

export const grantRole = async (userId, roleName) => {
  const response = await fetch(`${API_BASE_URL}/staff/users/${encodeURIComponent(userId)}/roles`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ role: roleName }),
  });

  if (!response.ok) {
    await raise(response, {
      404: 'That user or role no longer exists',
      409: 'That role is already granted manually',
    });
  }
};

export const revokeRole = async (userId, roleName) => {
  const response = await fetch(
    `${API_BASE_URL}/staff/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(roleName)}`,
    { method: 'DELETE', headers: getAuthHeaders() },
  );

  if (!response.ok) {
    await raise(response, {
      404: 'There is no manual grant of that role to revoke',
    });
  }
};
